import './styles/tokens.css';
import './styles/base.css';
import './styles/shell.css';
import './styles/surface.css';
import './styles/form.css';
import './styles/nav.css';
import './styles/icons.css';
import './styles/health.css';
import './styles/login.css';
import './styles/sessions.css';
import './styles/me.css';
import './styles/comingsoon.css';
import './styles/dialog.css';
import './styles/passkey.css';
import './styles/passkeys.css';
import './styles/recovery.css';
import './styles/error.css';
import './styles/home.css';
import './styles/contact.css';
import './styles/privacy.css';
import './styles/locale.css';

import { AppShell } from './components/AppShell';
import { HomePage } from './pages/HomePage';
import { ContactPage } from './pages/ContactPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { LoginPage } from './pages/LoginPage';
import { AuthVerifyPage } from './pages/AuthVerifyPage';
import { RecoveryPage } from './pages/RecoveryPage';
import { SessionsPage } from './pages/SessionsPage';
import { PasskeysPage } from './pages/PasskeysPage';
import { MePage } from './pages/MePage';
import { ComingSoonPage } from './pages/ComingSoonPage';
import { ErrorScreen } from './pages/ErrorScreen';
import {
  initRouter,
  registerRoute,
  setAuthGuard,
  setNotFound,
  type Component,
} from './router';
import { authInit, isAuthenticated } from './services/auth';
import { canViewPath } from './services/capabilities';
import { checkHealth } from './api/client';
import { api } from './services/api';
import { promptPasskeyEnroll } from './components/PasskeyModal';
import { getDeviceClass, setRootDeviceClass } from './utils/viewport';
import { setRootSurface } from './utils/surface';
import { initErrlog } from './utils/errlog';
import { getStoredTheme, setTheme } from './utils/theme';
import { initLocale } from './i18n/locale';

function wrap(inner: Component): Component {
  return new AppShell(inner);
}

// OAuth callbacks land on a normal route, not a dedicated page: the
// vfco-auth-package callback redirects to {redirect}?auth=success (the refresh
// cookie is already set, so authInit() below silently exchanges it for a token)
// or {redirect}?auth=error&code=CODE on failure. Normalize the failure to
// /login?error=CODE so LoginPage renders the message, and strip the success
// marker so it doesn't linger in the address bar. Runs before routing.
function handleOAuthCallback(): void {
  const params = new URLSearchParams(window.location.search);
  const auth = params.get('auth');
  if (!auth) return;
  if (auth === 'error') {
    const code = params.get('code') ?? 'OAUTH_PROVIDER_ERROR';
    window.history.replaceState({}, '', `/login?error=${encodeURIComponent(code)}`);
    return;
  }
  params.delete('auth');
  params.delete('code');
  const qs = params.toString();
  window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
}

// On first desktop sign-in for a sensitive role, offer passkey enrollment
//. Best-effort and non-blocking: the GET status poll already ran for
// the SPA, and any failure here must never wedge boot. Phones are exempt, so we
// skip the poll entirely off desktop.
async function maybePromptPasskeyEnroll(): Promise<void> {
  if (!isAuthenticated() || getDeviceClass() !== 'desktop') return;
  try {
    const status = await api<{ webauthn?: { enrollmentRequired?: boolean } }>('/api/auth/step-up');
    if (status.webauthn?.enrollmentRequired) {
      await promptPasskeyEnroll();
    }
  } catch {
    // No prompt on a failed poll — enrollment can be offered from settings later.
  }
}

initErrlog();
setRootDeviceClass();
setRootSurface();
setTheme(getStoredTheme());

registerRoute('/', () => wrap(HomePage()));
registerRoute('/contact', () => wrap(new ContactPage()));
registerRoute('/privacy', () => wrap(new PrivacyPage()));
registerRoute('/module-a', () =>
  wrap(
    ComingSoonPage({
      icon: 'capture',
      title: 'Module A',
      copy: 'This is an example module. Wire your own feature here.',
    }),
  ));
registerRoute('/module-b', () =>
  wrap(
    ComingSoonPage({
      icon: 'cases',
      title: 'Module B',
      copy: 'This is an example module. Wire your own feature here.',
    }),
  ));
registerRoute('/reports', () =>
  wrap(
    ComingSoonPage({
      icon: 'reports',
      title: 'Reports',
      copy: 'This is an example module. Wire your own feature here.',
    }),
  ));
registerRoute('/me', () => wrap(MePage()));
registerRoute('/settings/devices', () => wrap(SessionsPage()));
registerRoute('/settings/passkeys', () => wrap(PasskeysPage()));
registerRoute('/login', () => new LoginPage(), true);
registerRoute('/auth/verify', () => new AuthVerifyPage(), true);
registerRoute('/recovery', () => new RecoveryPage(), true);

setNotFound(() => wrap(new ErrorScreen({ kind: 'notFound' })));
setAuthGuard((path) => {
  // Public marketing surfaces (home, contact) and the auth entry points are
  // open to signed-out visitors. Everything else requires auth — the site is
  // otherwise admin-only.
  const publicPaths = new Set(['/', '/contact', '/privacy', '/login', '/auth/verify', '/recovery']);
  if (publicPaths.has(path)) return { allowed: true };
  if (!isAuthenticated()) return { allowed: false, redirectTo: '/login' };
  // Authenticated: mirror the server RBAC policy so a module this
  // user's role + device class can't reach lands on the friendly forbidden
  // screen in place — rather than a dead nav target or an empty shell. Advisory
  // only: the server re-authorizes every protected call, so this never grants
  // access, it only avoids dead ends.
  if (!canViewPath(path)) {
    return { allowed: false, render: () => wrap(new ErrorScreen({ kind: 'forbidden' })) };
  }
  return { allowed: true };
});

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register('/sw.js').catch(() => {
    // SW registration is best-effort — the app works without it.
  });
}

const app = document.getElementById('app');
if (app) {
  void (async () => {
    handleOAuthCallback();
    await initLocale();
    await authInit();
    initRouter(app);
    void checkHealth();
    void maybePromptPasskeyEnroll();
  })();
}
